import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import { getCookie, eraseCookie } from '../../../infrastructure/utils/cookieStorage';
import { dashboardService } from '../../../infrastructure/services/dashboardService';
import enrollmentService from '../../../infrastructure/services/enrollmentService';
import { CustomBreadcrumbs, PageBanner, KpiCard, ConfirmationModal, SuccessModal, ClassInvitationModal, ToastMessage } from '../../common';
import dashboardBannerBg from '../../../assets/images/dashboard_banner.jpg';
import ConcentricRings from './ConcentricRings';
import GaugeChart from './GaugeChart';
import WaveChart from './WaveChart';
import UpcomingClasses from './UpcomingClasses';
import ActiveCourses from './ActiveCourses';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [topupsRange, setTopupsRange] = useState('This Month');
  const [spendingsRange, setSpendingsRange] = useState('This Year');
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [classCode, setClassCode] = useState('');
  const [joiningByCode, setJoiningByCode] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [enrollCode, setEnrollCode] = useState(null);
  const [enrollClassInfo, setEnrollClassInfo] = useState(null);
  const [isEnrollLoading, setIsEnrollLoading] = useState(false);
  const [enrollError, setEnrollError] = useState(null);
  const [isJoining, setIsJoining] = useState(false);

  const [points] = useState(() => {
    try {
      return getCookie('userPoints') || '0';
    } catch (e) {
      return '0';
    }
  });

  // Dialog visibility controls
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  const handleClassCodeChange = (e) => {
    const rawVal = e.target.value;
    const cleanVal = rawVal.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setClassCode(cleanVal);
  };

  const handleJoinClassByCode = async () => {
    if (classCode.length !== 6) return;

    setJoiningByCode(true);
    try {
      const response = await enrollmentService.getEnrollInfo(classCode);
      if (response && response.isSuccessful && response.content) {
        setEnrollCode(classCode);
        setEnrollClassInfo(response.content);
        setClassCode('');
      } else {
        setToast({
          open: true,
          message: response?.message || 'Invalid class code. Please check and try again.',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error checking class code:', err);
      setToast({
        open: true,
        message: err.message || 'Failed to check class code. Please try again.',
        severity: 'error'
      });
    } finally {
      setJoiningByCode(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getStudentDashboard();
      if (response && response.isSuccessful) {
        setDashboard(response.content);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    const code = localStorage.getItem('pending_enroll_code');
    if (code) {
      setEnrollCode(code);
      fetchEnrollInfo(code);
    }
  }, []);

  // Fetch classroom details (teacher, subject, grade, fee, schedule) using enrollment code
  const fetchEnrollInfo = async (code) => {
    setIsEnrollLoading(true);
    setEnrollError(null);
    try {
      const response = await enrollmentService.getEnrollInfo(code);
      if (response && response.isSuccessful && response.content) {
        setEnrollClassInfo(response.content);
      } else {
        setEnrollError(response?.message || 'Invalid enrollment code.');
      }
    } catch (err) {
      console.error('Error fetching enroll info:', err);
      setEnrollError(err.message || 'Failed to fetch class details.');
    } finally {
      setIsEnrollLoading(false);
    }
  };

  // Submit enrollment request to the server to register student in the class
  const handleJoinClassByLink = async () => {
    if (!enrollCode) return;
    setIsJoining(true);
    try {
      const response = await enrollmentService.enrollByLink(enrollCode);
      if (response && response.isSuccessful) {
        localStorage.removeItem('pending_enroll_code');
        // Clear invitation preview dialog variables
        setEnrollCode(null);
        setEnrollError(null);

        // Open the success confirmation popup
        setIsSuccessDialogOpen(true);
        // Refresh dashboard courses and stats list
        fetchDashboard();
      } else {
        setEnrollError(response?.message || 'Failed to join class.');
      }
    } catch (err) {
      console.error('Error joining class:', err);
      setEnrollError(err.message || 'Failed to join class.');
    } finally {
      setIsJoining(false);
    }
  };

  const handleCloseEnroll = () => {
    localStorage.removeItem('pending_enroll_code');
    setEnrollCode(null);
    setEnrollClassInfo(null);
    setEnrollError(null);
  };



  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Dashboard' }
  ];

  const handleLogoutClick = () => {
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setIsLogoutDialogOpen(false);
    eraseCookie('authToken');
    eraseCookie('student_auth_token');
    eraseCookie('student_user');
    eraseCookie('studentId');
    eraseCookie('userPoints');
    navigate('/');
  };

  const kpiData = [
    {
      title: 'Assigned Exams',
      value: loading ? <Skeleton variant="text" width="60%" height={32} sx={{ borderRadius: '6px' }} /> : `${dashboard?.totalAssignedExams ?? 0} Papers`,
      changeText: loading ? <Skeleton variant="text" width="80%" height={16} sx={{ borderRadius: '4px' }} /> : `Across ${dashboard?.totalTeacherCount ?? 0} teachers`,
      color: '#8B5CF6',
      bg: 'linear-gradient(180deg, rgba(150, 100, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
      border: '#A78BFA',
      arrow: false
    },
    {
      title: 'Completed Exams',
      value: loading ? <Skeleton variant="text" width="60%" height={32} sx={{ borderRadius: '6px' }} /> : `${dashboard?.totalCompletedExams ?? 0} Graded`,
      changeText: loading ? <Skeleton variant="text" width="80%" height={16} sx={{ borderRadius: '4px' }} /> : `${dashboard?.examPerformance?.thisMonthAveragePercentage ?? 0}% avg this month`,
      color: '#10B981',
      bg: 'linear-gradient(180deg, rgba(152, 255, 100, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
      border: '#4EB860',
      arrow: false
    },
    {
      title: 'Academic Points',
      value: loading ? <Skeleton variant="text" width="60%" height={32} sx={{ borderRadius: '6px' }} /> : parseFloat(points).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      changeText: 'Top up from wallet',
      color: '#EC4899',
      bg: 'linear-gradient(180deg, rgba(244, 63, 94, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
      border: '#FDA4AF',
      arrow: false
    },
    {
      title: 'My Teachers',
      value: loading ? <Skeleton variant="text" width="60%" height={32} sx={{ borderRadius: '6px' }} /> : `${dashboard?.totalTeacherCount ?? 0} Teachers`,
      changeText: loading ? <Skeleton variant="text" width="80%" height={16} sx={{ borderRadius: '4px' }} /> : `Join to ${new Set((dashboard?.myTeachers || []).flatMap(t => t.subjects)).size} subjects`,
      color: '#3B82F6',
      bg: 'linear-gradient(180deg, rgba(100, 139, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
      border: '#5B92FF',
      arrow: false
    }
  ];

  const gaugeValue = dashboard?.examPerformance?.overallAveragePercentage ?? 0;
  const thisMonthAvg = dashboard?.examPerformance?.thisMonthAveragePercentage ?? 0;

  return (
    <Box sx={{ pb: 5 }}>
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      <PageBanner
        title="Student Dashboard"
        description="Access your exams, academic progress, wallet balance, and teacher information here."
        backgroundImage={dashboardBannerBg}
      >
        <Button
          variant="outlined"
          onClick={handleLogoutClick}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.65)',
            color: '#ffffff',
            borderRadius: '9999px',
            px: 3.5,
            py: 1,
            fontSize: '14.5px',
            fontWeight: 600,
            textTransform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              borderColor: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          Log Out
        </Button>
      </PageBanner>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, sm: 2.5, md: 3 },
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 2fr 1.2fr' },
          alignItems: 'stretch',
          mb: 4
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: '6px',
            border: '1.5px solid #FFE3C3',
            borderRadius: '24px',
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
            bgcolor: '#ffffff',
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(180deg, rgba(255, 161, 117, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
              borderRadius: '18px',
              p: 2.2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexGrow: 1,
              height: '100%',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '-30px',
                right: '-30px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, transparent 70%)',
                pointerEvents: 'none'
              }}
            />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '38px',
                    height: '38px',
                    borderRadius: '12px',
                    bgcolor: 'rgba(249, 115, 22, 0.08)',
                    color: '#f97316'
                  }}
                >
                  <GroupAddRoundedIcon sx={{ fontSize: '20px', color: '#f97316' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#1e293b', fontFamily: 'Poppins' }}>
                    Join Class
                  </Typography>
                  <Typography sx={{ color: '#64748b', fontSize: '12px', fontWeight: 550, fontFamily: 'Poppins' }}>
                    Enter class code from teacher
                  </Typography>
                </Box>
              </Stack>

              <Typography sx={{ color: '#475569', fontSize: '12px', fontFamily: 'Poppins', mb: 2, lineHeight: 1.4 }}>
                Enter the 6-character alphanumeric code below.
              </Typography>

              <Box
                sx={{
                  px: 1,
                  my: 1,
                  '& input::placeholder': {
                    color: '#94a3b8',
                    opacity: 1
                  }
                }}
              >
                <input
                  type="text"
                  value={classCode}
                  onChange={handleClassCodeChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && classCode.length === 6 && !joiningByCode) {
                      handleJoinClassByCode();
                    }
                  }}
                  placeholder="ENTER CODE"
                  maxLength={6}
                  style={{
                    width: '100%',
                    height: '44px',
                    textAlign: 'center',
                    fontSize: '16px',
                    fontWeight: '700',
                    letterSpacing: '3px',
                    borderRadius: '12px',
                    border: classCode.length === 6 ? '2px solid #f97316' : '1.5px solid #cbd5e1',
                    background: classCode.length === 6 ? 'rgba(249, 115, 22, 0.05)' : '#ffffff',
                    color: '#1e293b',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Poppins',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#f97316';
                    e.target.style.boxShadow = '0 0 8px rgba(249, 115, 22, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = classCode.length === 6 ? '#f97316' : '#cbd5e1';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={handleJoinClassByCode}
              disabled={classCode.length !== 6 || joiningByCode}
              sx={{
                mt: 1.5,
                height: '42px',
                borderRadius: '12px',
                fontFamily: 'Poppins',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #f97316 0%, #ff9f59 100%)',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                  boxShadow: '0 6px 16px rgba(249, 115, 22, 0.3)'
                },
                '&.Mui-disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8',
                  boxShadow: 'none'
                }
              }}
            >
              {joiningByCode ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                  <span>Checking...</span>
                </Stack>
              ) : (
                'Join Class'
              )}
            </Button>
          </Box>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            height: '100%'
          }}
        >
          {kpiData.map((kpi, idx) => (
            <KpiCard
              key={idx}
              title={kpi.title}
              value={kpi.value}
              footerText={kpi.changeText}
              borderColor={kpi.border}
              bgGradient={kpi.bg}
              showArrow={kpi.arrow}
              onClick={() => { }}
            />
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: '1.5px solid #e2e8f0',
            borderRadius: '24px',
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', fontFamily: 'Poppins', mb: 0.5 }}>
            Wallet Overview
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flex: 1,
              gap: 2
            }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#F8C07F' }} />
                  <Typography sx={{ color: '#64748b', fontSize: '12px', fontWeight: 550, fontFamily: 'Poppins', whiteSpace: 'nowrap' }}>
                    Balance
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', ml: 2.5, fontFamily: 'Poppins', lineHeight: 1.2 }}>
                  {loading ? <Skeleton variant="text" width="60px" sx={{ borderRadius: '4px' }} /> : parseFloat(points).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FB896B' }} />
                  <Typography sx={{ color: '#64748b', fontSize: '12px', fontWeight: 550, fontFamily: 'Poppins', whiteSpace: 'nowrap' }}>
                    Spent
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', ml: 2.5, fontFamily: 'Poppins', lineHeight: 1.2 }}>
                  {loading ? <Skeleton variant="text" width="60px" sx={{ borderRadius: '4px' }} /> : Number(dashboard?.walletStats?.totalSpent ?? 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#6956E5' }} />
                  <Typography sx={{ color: '#64748b', fontSize: '12px', fontWeight: 550, fontFamily: 'Poppins', whiteSpace: 'nowrap' }}>
                    Total Loaded
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', ml: 2.5, fontFamily: 'Poppins', lineHeight: 1.2 }}>
                  {loading ? <Skeleton variant="text" width="60px" sx={{ borderRadius: '4px' }} /> : Number(dashboard?.walletStats?.totalLoaded ?? 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            </Stack>

            <ConcentricRings />
          </Box>
        </Paper>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, sm: 2.5, md: 3 },
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          alignItems: 'stretch',
          mb: 4
        }}
      >
        <UpcomingClasses
          exams={dashboard?.recentExams || []}
          totalAssigned={dashboard?.totalAssignedExams || 0}
          loading={loading}
        />
        <ActiveCourses
          teachers={dashboard?.myTeachers || []}
          loading={loading}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, sm: 2.5, md: 3 },
          gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
          alignItems: 'stretch',
          mb: 4
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid #EEEEEE',
            borderRadius: '24px',
            height: '380px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '4px 4px 18px -5px #DADDE8CC'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#5E5E5E', fontFamily: 'Poppins' }}>
                Exam Performance
              </Typography>
              <Typography sx={{ color: '#979797', fontSize: '12px', fontFamily: 'Poppins' }}>
                Average score across all exams
              </Typography>
            </Box>

            <FormControl size="small">
              <Select
                value={topupsRange}
                onChange={(e) => setTopupsRange(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                sx={{
                  borderRadius: '12px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#475569',
                  bgcolor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#64748b' },
                  height: '34px',
                  pl: 0.5
                }}
              >
                <MenuItem value="This Month" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>This Month</MenuItem>
                <MenuItem value="Overall" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Overall</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ py: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 180 }}>
                <CircularProgress size={40} sx={{ color: '#8B5CF6' }} />
              </Box>
            ) : (
              <GaugeChart value={topupsRange === 'This Month' ? thisMonthAvg : gaugeValue} />
            )}
          </Box>

          <Box
            sx={{
              borderTop: '1px solid #f1f5f9',
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ justifyContent: 'center' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8B5CF6' }} />
              <Typography sx={{ color: '#64748b', fontSize: '12px', fontWeight: 550, fontFamily: 'Poppins' }}>
                Average exam score
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', mt: 0.25, fontFamily: 'Poppins', textAlign: 'center' }}>
              {loading ? '—' : `${topupsRange === 'This Month' ? thisMonthAvg : gaugeValue}% Average`}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid #EEEEEE',
            borderRadius: '24px',
            height: '380px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '4px 4px 18px -5px #DADDE8CC'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#343C6A', fontFamily: 'Poppins' }}>
                Exam Score Trend
              </Typography>
              <Typography sx={{ color: '#787486', fontSize: '12px', fontFamily: 'Poppins' }}>
                Monthly exam performance scores
              </Typography>
            </Box>

            <FormControl size="small">
              <Select
                value={spendingsRange}
                onChange={(e) => setSpendingsRange(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                sx={{
                  borderRadius: '12px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#475569',
                  bgcolor: '#f8fafc',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#64748b' },
                  height: '34px',
                  pl: 0.5
                }}
              >
                <MenuItem value="This Year" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>This Year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', mt: 2, width: '100%' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <CircularProgress size={40} sx={{ color: '#4F46E5' }} />
              </Box>
            ) : (
              <WaveChart data={dashboard?.examScoreTrend || []} />
            )}
          </Box>
        </Paper>
      </Box>

      <ConfirmationModal
        isOpen={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
        title="Confirm Logout"
        description="Are you sure you want to log out of your account? You will need to sign in again to access the portal."
        confirmText="Logout"
        cancelText="Cancel"
        type="danger"
      />

      {/* 1. CLASS ENROLLMENT INVITATION DIALOG                */}
      <ClassInvitationModal
        isOpen={Boolean(enrollCode)}
        onClose={handleCloseEnroll}
        onJoin={() => setIsConfirmDialogOpen(true)}
        isEnrollLoading={isEnrollLoading}
        enrollError={enrollError}
        enrollClassInfo={enrollClassInfo}
        isJoining={isJoining}
      />

      {/* 2. JOIN CLASS CONFIRMATION MODAL                     */}
      <ConfirmationModal
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={() => {
          setIsConfirmDialogOpen(false);
          handleJoinClassByLink();
        }}
        title="Confirm Enrollment"
        description={enrollClassInfo ? `Are you sure you want to join "${enrollClassInfo.title}" conducted by ${enrollClassInfo.teacherName}? You will be enrolled in this class.` : "Are you sure you want to join this class?"}
        confirmText="Join Class"
        cancelText="Cancel"
        type="info"
      />

      {/* 3. ENROLLMENT SUCCESS NOTIFICATION MODAL             */}
      <SuccessModal
        isOpen={isSuccessDialogOpen}
        onClose={() => {
          setIsSuccessDialogOpen(false);
          setEnrollCode(null);
          setEnrollClassInfo(null);
          setEnrollError(null);
        }}
        title="Enrolled Successfully!"
        description={enrollClassInfo ? `You have successfully joined "${enrollClassInfo.title}" by ${enrollClassInfo.teacherName}. You can now access all classroom materials, live sessions, and exams.` : "You have successfully joined the class."}
        actionText="Go to Exams"
        onAction={() => {
          setIsSuccessDialogOpen(false);
          setEnrollCode(null);
          setEnrollClassInfo(null);
          setEnrollError(null);
          navigate('/exam-manager');
        }}
      />
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default DashboardPage;
